import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (d) => {
  if (!d) return '—'
  const now  = new Date()
  const date = new Date(d)
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60)   return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400)return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return date.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
}
const fmtFull = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

// ── Notification type config ──────────────────────────────────────────────────
const TYPES = {
  order:      { icon: '🛍',  color: '#1d4ed8', bg: '#eff6ff', label: 'Order'       },
  payment:    { icon: '💳',  color: '#15803d', bg: '#f0fdf4', label: 'Payment'     },
  shop:       { icon: '🏪',  color: '#7c3aed', bg: '#f5f3ff', label: 'Shop'        },
  driver:     { icon: '🚚',  color: '#b45309', bg: '#fffbeb', label: 'Driver'      },
  user:       { icon: '👤',  color: '#0369a1', bg: '#f0f9ff', label: 'User'        },
  reel:       { icon: '▶',   color: '#7c3aed', bg: '#f5f3ff', label: 'Clip'        },
  system:     { icon: '⚙',   color: '#6b7280', bg: '#f3f4f6', label: 'System'      },
  alert:      { icon: '⚠️',  color: '#b91c1c', bg: '#fef2f2', label: 'Alert'       },
}
const nt = (type) => TYPES[type?.toLowerCase()] ?? { icon: '🔔', color: '#6b7280', bg: '#f3f4f6', label: type || 'Notification' }

// Target audience config
const TARGETS = [
  { value: 'all',     label: 'All Users',    icon: '👥' },
  { value: 'buyers',  label: 'Buyers Only',  icon: '🛒' },
  { value: 'sellers', label: 'Sellers Only', icon: '🏪' },
  { value: 'drivers', label: 'Drivers Only', icon: '🚚' },
]

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp = {
  width: '100%', height: 42, borderRadius: 8, border: '1px solid #e9e9e4',
  padding: '0 13px', fontSize: 13, fontWeight: 500, color: '#111317',
  outline: 'none', background: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif",
  boxSizing: 'border-box',
}
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#111317',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
  fontFamily: "'JetBrains Mono',monospace",
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 7 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

// ── Compose Modal ─────────────────────────────────────────────────────────────
function ComposeModal({ onClose, onSent }) {
  const [form, setForm] = useState({
    title:   '',
    message: '',
    type:    'system',
    target:  'all',
    user_id: '',     // specific user (optional)
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const [preview, setPreview] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSend = async (e) => {
    e.preventDefault()
    setLoading(true); setError('')
    try {
      const payload = {
        title:   form.title,
        message: form.message,
        type:    form.type,
        target:  form.user_id ? 'user' : form.target,
        user_id: form.user_id || null,
      }
      const res = await api.post('/notifications/admin/send', payload)
      onSent(res.data)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send notification')
    } finally {
      setLoading(false)
    }
  }

  const typeStyle = nt(form.type)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 520, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Send Notification</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>Push to users in real time</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPreview(!preview)}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: preview ? '#111317' : '#fff', color: preview ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {preview ? 'Edit' : 'Preview'}
            </button>
            <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
          </div>
        </div>

        {preview ? (
          /* Preview pane — looks like a phone notification */
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 14 }}>Preview</div>
            <div style={{ background: '#111317', borderRadius: 16, padding: 20, maxWidth: 340, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: typeStyle.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{typeStyle.icon}</div>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: "'JetBrains Mono',monospace" }}>KUDOS LIVE · now</div>
                </div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 5 }}>{form.title || 'Notification Title'}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{form.message || 'Your message will appear here.'}</div>
              <div style={{ marginTop: 12, fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
                → {TARGETS.find(t => t.value === form.target)?.label || 'All Users'}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSend}>
            <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Type + Target */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={lbl}>Type</label>
                  <select value={form.type} onChange={e => set('type', e.target.value)}
                    style={{ ...inp, cursor: 'pointer' }}>
                    {Object.entries(TYPES).map(([k, v]) => (
                      <option key={k} value={k}>{v.icon} {v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Send To</label>
                  <select value={form.target} onChange={e => set('target', e.target.value)}
                    style={{ ...inp, cursor: 'pointer' }}>
                    {TARGETS.map(t => (
                      <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Specific user */}
              <div>
                <label style={lbl}>Specific User ID <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#9a9a94' }}>(optional — overrides Send To)</span></label>
                <input value={form.user_id} onChange={e => set('user_id', e.target.value)}
                  placeholder="Leave blank to send to group above" style={inp} />
              </div>

              {/* Title */}
              <div>
                <label style={lbl}>Title *</label>
                <input required value={form.title} onChange={e => set('title', e.target.value)}
                  placeholder="Notification title" style={inp} />
              </div>

              {/* Message */}
              <div>
                <label style={lbl}>Message *</label>
                <textarea required value={form.message} onChange={e => set('message', e.target.value)}
                  placeholder="Write your message here…" rows={4}
                  style={{ ...inp, height: 'auto', padding: '10px 13px', resize: 'vertical', lineHeight: 1.6 }} />
                <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, textAlign: 'right', fontFamily: "'JetBrains Mono',monospace" }}>
                  {form.message.length} chars
                </div>
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>
              )}
            </div>

            <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose}
                style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                Cancel
              </button>
              <button type="submit" disabled={loading}
                style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                {loading ? 'Sending…' : `🔔 Send to ${form.user_id ? 'User' : TARGETS.find(t => t.value === form.target)?.label}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

// ── Notification Card ─────────────────────────────────────────────────────────
function NotifCard({ notif, onDelete }) {
  const t = nt(notif.type)
  return (
    <div style={{
      background: notif.read_at ? '#fff' : '#fffbf8',
      border: `1px solid ${notif.read_at ? '#e9e9e4' : '#f3d9c8'}`,
      borderLeft: `3px solid ${notif.read_at ? '#e9e9e4' : t.color}`,
      borderRadius: 10, padding: '14px 16px',
      opacity: notif.deleted ? 0.4 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Type icon */}
        <div style={{ width: 36, height: 36, borderRadius: 9, background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
          {t.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 800 }}>{notif.title}</span>
            {/* Type badge */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: t.bg, color: t.color, fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase' }}>
              {t.label}
            </span>
            {/* Target */}
            <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
              → {notif.target === 'user' ? `User #${notif.user_id}` : (TARGETS.find(t => t.value === notif.target)?.label || notif.target)}
            </span>
            {!notif.read_at && (
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#e8440a', display: 'inline-block' }} />
            )}
          </div>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 8 }}>{notif.message}</div>

          {/* Footer meta */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }} title={fmtFull(notif.created_at)}>
              {fmtTime(notif.created_at)}
            </span>
            {notif.sent_count != null && (
              <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                📨 {notif.sent_count?.toLocaleString()} sent
              </span>
            )}
            {notif.read_count != null && (
              <span style={{ fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>
                👁 {notif.read_count?.toLocaleString()} read
              </span>
            )}
            {notif.sent_by_name && (
              <span style={{ fontSize: 11, color: '#9a9a94' }}>by {notif.sent_by_name}</span>
            )}
          </div>
        </div>

        {/* Delete */}
        <button onClick={() => onDelete(notif)}
          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", flexShrink: 0 }}>
          Del
        </button>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Notifications() {
  const [notifs,       setNotifs]       = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [typeFilter,   setTypeFilter]   = useState('')
  const [targetFilter, setTargetFilter] = useState('')
  const [search,       setSearch]       = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total: 0, unread: 0, today: 0, total_sent: 0 })
  const [showCompose,  setShowCompose]  = useState(false)

  const PER_PAGE = 20

  const fetchNotifs = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(typeFilter   && { type:   typeFilter }),
        ...(targetFilter && { target: targetFilter }),
        ...(search       && { search }),
      })
      const res = await api.get(`/notifications/admin/all?${params}`)
      setNotifs(res.data.notifications || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats || { total: 0, unread: 0, today: 0, total_sent: 0 })
    } catch {
      setError('Failed to load — showing mock data')
      const types   = Object.keys(TYPES)
      const targets = ['all','buyers','sellers','drivers','user']
      const mock = Array.from({ length: 14 }, (_, i) => ({
        notification_id: i + 1,
        title:           ['New Order Placed 🛍', 'Payment Confirmed ✓', 'Driver Assigned 🚚', 'Welcome to Kudos!', 'New Shop Approved'][i % 5],
        message:         i % 3 === 0
          ? 'Your order #1024 has been placed successfully and is awaiting confirmation from the seller.'
          : 'This is a sample notification message for testing purposes.',
        type:            types[i % types.length],
        target:          targets[i % targets.length],
        user_id:         targets[i % targets.length] === 'user' ? i + 100 : null,
        read_at:         i % 3 === 0 ? new Date(Date.now() - i * 3600000).toISOString() : null,
        sent_count:      Math.floor(Math.random() * 5000),
        read_count:      Math.floor(Math.random() * 3000),
        sent_by_name:    'Admin',
        created_at:      new Date(Date.now() - i * 7200000).toISOString(),
      }))
      setNotifs(mock)
      setTotalPages(3); setTotalCount(60)
      setStats({ total: 60, unread: 18, today: 8, total_sent: 142000 })
    } finally { setLoading(false) }
  }, [page, typeFilter, targetFilter, search])

  useEffect(() => { fetchNotifs() }, [fetchNotifs])
  useEffect(() => { setPage(1) }, [typeFilter, targetFilter, search])

  const handleDelete = async (notif) => {
    if (!window.confirm('Delete this notification?')) return
    try {
      await api.delete(`/notifications/admin/${notif.notification_id}`)
      setNotifs(prev => prev.filter(n => n.notification_id !== notif.notification_id))
      setTotalCount(c => c - 1)
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete') }
  }

  const handleSent = (data) => {
    fetchNotifs()
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Notifications</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Send push notifications to users and view history.</div>
        </div>
        <button onClick={() => setShowCompose(true)}
          style={{ height: 40, padding: '0 20px', borderRadius: 9, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(232,68,10,0.25)' }}>
          🔔 Send Notification
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Sent"   value={stats.total.toLocaleString()}                              />
        <StatCard label="Unread"       value={stats.unread.toLocaleString()}      accent="#e8440a"       />
        <StatCard label="Today"        value={stats.today.toLocaleString()}       accent="#1d4ed8"       />
        <StatCard label="Total Reach"  value={stats.total_sent?.toLocaleString()} accent="#15803d"       />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '11px 16px', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search title or message…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>

        {/* Type filter */}
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Types</option>
          {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>

        {/* Target filter */}
        <select value={targetFilter} onChange={e => setTargetFilter(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Targets</option>
          {TARGETS.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>

        <button onClick={fetchNotifs} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} notifications</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Notifications list */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading notifications…</div>
      ) : notifs.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '60px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No notifications yet</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 20 }}>Send your first notification to users.</div>
          <button onClick={() => setShowCompose(true)}
            style={{ height: 40, padding: '0 20px', borderRadius: 8, border: 'none', background: '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            🔔 Send Notification
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {notifs.map(notif => (
            <NotifCard key={notif.notification_id} notif={notif} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Compose modal */}
      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} onSent={handleSent} />}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}